/**
 * stdCanvasStyles.js
 * ----------------------------------------------------------------------------
 * FONTE ÚNICA do CSS de renderização do desenho técnico (classes .std-*).
 *
 * Por que existe: o CSS scoped do DrawingCanvas não viaja para a janela de
 * impressão (que é montada via document.write). Antes, SaleView reinjetava
 * uma lista de classes copiada à mão — que dessincronizava a cada mudança.
 * Agora o canvas E a impressão consomem esta mesma string:
 *
 *  - No app: `installStdStyles()` (chamado no mounted do DrawingCanvas)
 *    injeta um <style id="std-canvas-styles"> no <head> uma única vez.
 *  - Na impressão: `janela.document.write('<style>' + STD_CANVAS_CSS + ...)`.
 *
 * REGRA: toda classe .std-* que afeta o RENDER do desenho (traços, cotas,
 * textos, símbolos) mora aqui. No <style scoped> dos componentes ficam apenas
 * layout e interação (cursores, bordas do container, handles de edição).
 */

export const STD_CANVAS_CSS = `
/* TEXTOS: escalam por --std-text-scale (o canvas seta 1/zoom quando o zoom
   passa de 100%) — assim as letras têm um TETO e não crescem junto com a
   peça ao aproximar. Na impressão a var não existe => fallback 1. */

/* ===== cotas (padrão ABNT: chamada + linha com setas + texto) ===== */
.std-ext { stroke: #173b33; stroke-width: 0.7; }
.std-dimline { stroke: #173b33; stroke-width: 0.9; fill: none; }
.std-dim-bg { fill: #eef3f0; opacity: 0.9; }
.std-dim-text { font-size: calc(9px * var(--std-text-scale, 1)); font-weight: 700; fill: #173b33; }

/* ===== eixos (traço-ponto) de furos/recortes ===== */
.std-centerline { stroke: #666666; stroke-width: 0.8; }

/* ===== rótulos de peça ===== */
.std-piece-label { font-size: calc(11px * var(--std-text-scale, 1)); font-weight: 700; paint-order: stroke; }
.std-piece-sub { font-size: calc(9px * var(--std-text-scale, 1)); }

/* ===== elementos técnicos livres ===== */
.std-element-tag { font-size: calc(9px * var(--std-text-scale, 1)); fill: #555555; font-weight: 600; }
.std-element-text { font-size: calc(12px * var(--std-text-scale, 1)); }

/* ===== anotações (texto simples, discreto) ===== */
.std-annotation-text { fill: #37413d; }
.std-leader-line { stroke: #5a6660; stroke-width: 0.9; stroke-dasharray: 3 3; fill: none; }

/* ===== swatch do acabamento cadastrado na borda ===== */
.std-edge-swatch-frame { fill: #ffffff; stroke: #173b33; stroke-width: 0.8; }
.std-edge-swatch { cursor: pointer; }
.std-edge-swatch-name { font-size: calc(7.5px * var(--std-text-scale, 1)); font-weight: 700; fill: #173b33; letter-spacing: 0.3px; }

/* ===== sugestão-fantasma de peça secundária (saia/rodapé) ===== */
.std-ghost-suggestion { fill: rgba(0, 0, 0, 0.04); stroke: rgba(60, 70, 66, 0.5); stroke-width: 1; stroke-dasharray: 4 3; }
.std-ghost-suggestion-label { font-size: calc(8.5px * var(--std-text-scale, 1)); font-weight: 700; fill: #4a544f; letter-spacing: 0.3px; }

/* ===== recortes vinculados (cuba/cooktop/furos) ===== */
.std-cutout-outline { stroke: #173b33; stroke-width: 1.2; fill: #ffffff; }
.std-cutout-detail { stroke: #173b33; stroke-width: 0.8; fill: none; }
.std-cutout-label { font-size: calc(8.5px * var(--std-text-scale, 1)); font-weight: 700; fill: #173b33; letter-spacing: 0.4px; }

/* ===== acabamento de borda: decorações extras ===== */
.std-edge-decor { fill: none; }

/* ===== cota fantasma durante o redimensionamento ===== */
.std-ghost-dim { font-size: calc(10px * var(--std-text-scale, 1)); font-weight: 700; fill: #333333; }

/* ===== retângulo da BORRACHA (apagar partes da peça) ===== */
.std-eraser-rect { fill: rgba(0, 0, 0, 0.05); stroke: #555555; stroke-width: 1.2; stroke-dasharray: 3 3; }

/* ===== PAREDE (utility:wall): referência visual, NÃO entra no orçamento —
   por isso é a ÚNICA parte do desenho com cor (o resto é P&B line-art de
   propósito). A faixa (espessura real de alvenaria) é o PRÓPRIO wallPathD
   esticado com stroke-width = espessura + stroke-linejoin "miter" — o SVG
   nativo faz a quina em bico reto sozinho (sem geometria de offset manual,
   que antes arredondava/entortava o canto de 90°). Dois traços empilhados
   no mesmo atributo "d" do path: um mais grosso (borda, cor vem do JS via
   wallOutlineColor) por baixo, um mais fino por cima preenchido com a
   TEXTURA de tijolo (pattern "stdBrickPattern", definido no defs do
   DrawingCanvas). */
.std-wall-ribbon-outline,
.std-wall-ribbon-fill {
  fill: none;
  stroke-linejoin: miter;
  stroke-linecap: square;
  stroke-miterlimit: 6;
}
.std-wall-ribbon-fill { stroke: url(#stdBrickPattern); }
.std-wall-ribbon-outline--draft { stroke: #2d6cdf; opacity: 0.85; }
.std-wall-ribbon-fill--draft { stroke: rgba(45, 108, 223, 0.12); }
.std-wall-anchor { fill: #ffffff; stroke: #2d6cdf; stroke-width: 1.4; }
.std-wall-bow { fill: #ffffff; stroke: #2d6cdf; stroke-width: 1.2; }
.std-wall-angle-bg { fill: #eef3f0; opacity: 0.9; }
.std-wall-angle-text { font-size: calc(9px * var(--std-text-scale, 1)); font-weight: 700; fill: #5a6660; }
`;

/**
 * CSS exclusivo da PRANCHA IMPRESSA (janela de impressão). Complementa o
 * STD_CANVAS_CSS: reset do body, quebra de página por folha, ajustes de
 * fundo (papel branco) e @page.
 */
export const SHEET_PRINT_CSS = `
* { font-family: Arial, Helvetica, sans-serif; box-sizing: border-box; }
body { -webkit-print-color-adjust: exact; print-color-adjust: exact; margin: 0; background: #ffffff; }
.tech-sheet { page-break-after: always; }
.tech-sheet:last-child { page-break-after: auto; }
.tech-sheet .ts-drawing svg { width: 100%; height: auto; }
/* no papel o fundo é branco: a máscara atrás do texto da cota acompanha */
.tech-sheet .std-dim-bg,
.tech-sheet .std-wall-angle-bg { fill: #ffffff; }
@page { size: A4 landscape; margin: 0.6cm; }

/* ===== prancha (TechnicalSheet) ===== */
.ts-frame { border: 2px solid #173b33; padding: 6px; }
.ts-titlerow { display: flex; align-items: center; gap: 10px; border-bottom: 1.5px solid #173b33; padding: 2px 4px 6px; }
.ts-detail-number { width: 30px; height: 30px; border: 1.6px solid #173b33; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 13pt; color: #173b33; flex-shrink: 0; }
.ts-titles { display: flex; flex-direction: column; }
.ts-title { font-size: 13pt; font-weight: bold; color: #173b33; text-transform: uppercase; letter-spacing: 0.5px; }
.ts-scale { font-size: 8.5pt; color: #444; letter-spacing: 1px; }
.ts-material { margin-left: auto; display: flex; flex-direction: column; align-items: flex-end; font-size: 8.5pt; color: #444; }
.ts-drawing { border: 1px solid #173b33; margin-top: 6px; background: #fff; }
.ts-drawing .std-canvas { border: none; border-radius: 0; background: #ffffff; }
.ts-iso { border-style: dashed; border-color: #9aa7a0; }
.ts-footer { display: flex; gap: 8px; margin-top: 6px; align-items: stretch; }
.ts-table { flex: 1.2; border-collapse: collapse; font-size: 8pt; }
.ts-table th, .ts-table td { border: 1px solid #999; padding: 2px 4px; text-align: left; }
.ts-table th { background: #eef3f0; color: #173b33; }
.ts-table-price { text-align: right; white-space: nowrap; }
.ts-table-total-label { text-align: right; font-weight: bold; color: #173b33; }
.ts-table-total-value { font-weight: bold; background: #eef3f0; }
.ts-legend { flex: 1; border: 1px solid #173b33; padding: 4px 6px; font-size: 8pt; }
.ts-legend-item { display: flex; align-items: center; gap: 6px; margin-top: 2px; }
.ts-finish-thumb { border: 1px solid #999; border-radius: 2px; height: 14px; object-fit: cover; width: 14px; }
.ts-notes { flex: 1.2; border: 1px solid #173b33; padding: 4px 6px; font-size: 8pt; }
.ts-note { margin-top: 2px; color: #333; }
.ts-alert { color: #b00020; font-weight: bold; }
.ts-block-title { font-size: 7.5pt; font-weight: bold; color: #173b33; letter-spacing: 0.6px; border-bottom: 1px solid #ccc; padding-bottom: 2px; }
.ts-mt { margin-top: 6px; }
.ts-muted { color: #888; margin-top: 2px; }
.ts-titleblock { display: flex; border: 1.5px solid #173b33; margin-top: 6px; }
.ts-tb-cell { flex: 1; border-right: 1px solid #173b33; padding: 4px 6px; min-height: 44px; }
.ts-tb-last { border-right: none; }
.ts-tb-brand { display: flex; align-items: center; gap: 6px; flex: 1.4; }
.ts-tb-logo { max-height: 34px; max-width: 60px; }
.ts-tb-label { font-size: 6.5pt; color: #666; letter-spacing: 0.8px; }
.ts-tb-strong { font-size: 9pt; font-weight: bold; color: #173b33; }
.ts-tb-small { font-size: 7pt; color: #555; }
`;

const STYLE_TAG_ID = "std-canvas-styles";

/**
 * Injeta o CSS de render no <head> do documento atual (idempotente).
 * Chamado no mounted do DrawingCanvas — qualquer tela que renderize o
 * desenho ganha os estilos sem depender de scoped CSS.
 */
export function installStdStyles(doc = document) {
  if (!doc) return;
  const existing = doc.getElementById(STYLE_TAG_ID);
  if (existing) {
    // atualiza se o CSS mudou (hot-reload/nova versão sem F5)
    if (existing.textContent !== STD_CANVAS_CSS) {
      existing.textContent = STD_CANVAS_CSS;
    }
    return;
  }
  const style = doc.createElement("style");
  style.id = STYLE_TAG_ID;
  style.textContent = STD_CANVAS_CSS;
  doc.head.appendChild(style);
}
